import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Coffee, Circle, UserX, Power, Users, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  available: { label: 'Available',  icon: Circle, text: 'text-emerald-600', bg: 'bg-emerald-50',  dot: 'bg-emerald-500',  ring: 'ring-emerald-300',  border: 'border-emerald-200', pulse: true  },
  on_break:  { label: 'On Break',   icon: Coffee, text: 'text-amber-700',   bg: 'bg-amber-50',    dot: 'bg-amber-500',    ring: 'ring-amber-300',    border: 'border-amber-200',  pulse: false },
  away:      { label: 'Away',       icon: UserX,  text: 'text-rose-700',    bg: 'bg-rose-50',     dot: 'bg-rose-500',     ring: 'ring-rose-300',     border: 'border-rose-200',   pulse: false },
  offline:   { label: 'Off Duty',   icon: Power,  text: 'text-slate-600',   bg: 'bg-slate-100',   dot: 'bg-slate-400',    ring: 'ring-slate-300',    border: 'border-slate-200',  pulse: false },
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const now = new Date();
    const mins = Math.floor((now - d) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' });
  } catch {
    return '—';
  }
};

/**
 * Live list of every clerk's current status (Available / On Break / Away / Off Duty).
 * Polls /api/clerks/statuses every 30s. Admin-facing widget.
 */
export default function ClerkStatusList() {
  const [clerks, setClerks] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchStatuses = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/clerks/statuses`, { headers });
      setClerks(res.data);
    } catch {}
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchStatuses();
    const iv = setInterval(fetchStatuses, 30000);
    return () => clearInterval(iv);
  }, [fetchStatuses]);

  const counts = clerks.reduce((acc, c) => {
    const s = c.status || 'available';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <Card className="mb-4" data-testid="clerk-status-list">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-indigo-500" />
            <h3 className="font-semibold">Clerks — Live Status</h3>
            <span className="text-xs text-slate-400">({clerks.length})</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {Object.entries(STATUS_META).map(([k, m]) => (
              <span key={k} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${m.bg} ${m.text} border ${m.border}`}>
                <span className={`h-2 w-2 rounded-full ${m.dot}`} />
                {counts[k] || 0} {m.label}
              </span>
            ))}
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={fetchStatuses} title="Refresh">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-sm text-slate-400 py-4">Loading...</div>
        ) : clerks.length === 0 ? (
          <div className="text-center text-sm text-slate-400 py-4">No clerks yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {clerks.map(c => {
              const meta = STATUS_META[c.status] || STATUS_META.available;
              const Icon = meta.icon;
              return (
                <div
                  key={c.id}
                  className={`flex items-center gap-3 p-2.5 rounded-lg border ${meta.border} ${meta.bg}`}
                  data-testid={`clerk-status-row-${c.username || c.email}`}
                >
                  <div className="relative flex-shrink-0">
                    {meta.pulse && <span className={`absolute inset-0 rounded-full ${meta.dot} opacity-60 animate-ping`} />}
                    <span className={`relative block h-3 w-3 rounded-full ${meta.dot} ring-2 ${meta.ring}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{c.full_name || c.username}</div>
                    <div className={`text-[11px] font-medium flex items-center gap-1 ${meta.text}`}>
                      <Icon className="h-3 w-3" />
                      {meta.label}
                      <span className="text-slate-400 font-normal">· {fmtTime(c.status_updated_at)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
